import { DarkMode, Description, LightMode, Logout } from "@mui/icons-material";
import Linkify from "react-linkify";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Link,
  Modal,
  SvgIcon,
  Toolbar,
  Typography,
  useTheme,
  Backdrop,
  Tooltip,
  Theme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import moment from "moment";
import { useEffect, useRef, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearHistory,
  clearMessages,
  selectHistory,
  sendMessage as dispatchMessage,
  updateHistory,
} from "../../features/messages/messagesSlice";
import { selectTheme, toggleTheme } from "../../features/theme/themeSlice";
import { useMessages } from "../../hooks/useMessages";
import {
  PayloadMessage,
  PayloadMessageType,
} from "../../model/payload-message";
import { useHttpEscalloChatService } from "../../service/http.escallo-chat.service";
import { Styles } from "../../types/styles";
import { BottomChat } from "./BottomChat";
import { useSearchParams } from "react-router-dom";
import {
  setAlreadyLoggedByParam,
  setCredentials,
  setCredentialsLoading,
} from "../../features/credentials/credentialsSlice";
import { clearCredentials } from "../../service/credentials-store.service";
import {
  selectChatState,
  setChatState,
} from "../../features/settings/settingsSlice";
import { Magnifier } from "react-image-magnifiers";
import { makeStyles } from "@mui/styles";

import VideoPlayer from "../../components/VideoPlayer";
import AudioPlayer from "../../components/AudioPlayer";
// import { Log } from "../../functions";

export type ChatProps = {};

const useStyles = makeStyles((theme: Theme) => ({
  backDrop: {
    backdropFilter: "blur(6px)",
    backgroundColor:'rgba(0,0,30,0.4)'
  },
}));

export function Chat({ socket, logoutEnabled }: any) {
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const chatState = useAppSelector(selectChatState);
  const [searchParams] = useSearchParams();
  // const nomeBotAtendimento = searchParams.get("nome_bot_atendimento");
  const boasVindas = searchParams.get("boas_vindas");
  // console.log('Boas vindas', boasVindas);
  const autoInicio = searchParams.get("auto_inicio");
  const darkModeEnabled = Boolean(
    parseFloat(searchParams.get("dark_mode_enabled") || "")
  );
  // const anonymousLogin = searchParams.get("login_anonimo");
  // const isAnonymousLogin = Number(anonymousLogin) === 1 ? true : false

  const httpService = useHttpEscalloChatService();

  async function onLogout() {
    window.top && window.top.postMessage('disconnected user', '*');
    
    dispatch(setCredentialsLoading(true));
    try {
      if (chatState) await httpService.closeSession();
      dispatch(setChatState(true));
      dispatch(setAlreadyLoggedByParam(true));
      dispatch(setCredentials(null));
      dispatch(clearHistory());
      dispatch(clearMessages());
      clearCredentials();
      socket.onDisconnect();
      socket.onConnect();
      dispatch(setCredentialsLoading(false));

    } catch (error) {
      dispatch(setCredentialsLoading(false));
    }
  }

  const escalloChatHttpService = useHttpEscalloChatService();
  const [imageModal, setImageModal] = useState("");
  const [chatLoading, setChatLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isThereHistory, setIsThereHistory] = useState(false);
  const [historyCounter, setHistoryCounter] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const history = useAppSelector(selectHistory);
  const isDarkMode = useAppSelector(selectTheme);

  const {
    messages: newMessages,
    sendMessage,
    sendImage,
    sendVideo,
    sendDocument,
  } = useMessages(socket.emitSocketMessage);

  const messages = [...history, ...newMessages];

  function onExpandImage(imageUrl: string | undefined) {
    // window.top && window.top.postMessage('expand image', '*');
    // window.top && window.top.postMessage(`${imageUrl ?? null}`, '*');
    setImageModal(imageUrl ? imageUrl : "");
  }

  const { theme, styles } = useChatTheme();
  
  async function getMessages(isFirstTime = false) {
    try {
      setIsLoadingHistory(true);
      const response = await escalloChatHttpService.getMessages(
        historyCounter * 20
      );
      if (autoInicio && response.data.length === 0) {
        sendMessage(`Iniciar atendimento`);
      }
      setHistoryCounter((prevState: number) => prevState + 1);
      dispatch(updateHistory(response.data.reverse()));
      setHistoryTotal(response.total);
      setIsLoadingHistory(false);
      if (isFirstTime) setChatLoading(false);
    } catch (error) {
      setIsLoadingHistory(false);
      console.error(error);
      if (isFirstTime) setChatLoading(false);
    }
  }

  useEffect(() => {
    if (history.length === 0 || history.length === historyTotal) {
      setIsThereHistory(false);
    } else {
      setIsThereHistory(true);
    }
  }, [historyTotal, history]);

  const subscribeMessageCallback = useCallback(
    (message: any) => {
      socket.emitDeliveredEvent(message);
      socket.emitReadEvent(message);
      dispatch(dispatchMessage(message));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [socket.current]
  );

  const subscribeMessageSentCallback = useCallback(
    (message: any) => {
      if (!(message.text === `Iniciar atendimento`)) {
        dispatch(dispatchMessage(message));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [socket.current]
  );

  useEffect(() => {
    setChatLoading(true);
    getMessages(true);
    // console.log("SUBSCRIBE TO MESSAGES");
    socket.subscribeMessage(subscribeMessageCallback);
    socket.subscribeMessageSent(subscribeMessageSentCallback);
    return () => {
      // console.log("UNSUBSCRIBE TO MESSAGES!");
      socket.unsubscribeMessage(subscribeMessageCallback);
      socket.unsubscribeMessageSent(subscribeMessageSentCallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket.current]);


  function Messages({ messages }: { messages: PayloadMessage[] }) {
    
    return (
      <>
        {isLoadingHistory && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginTop: "12px",
              marginBottom: "12px",
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {isThereHistory && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginTop: "12px",
              marginBottom: "12px",
            }}
          >
            <Button onClick={() => getMessages()}>
              Carregar mais mensagens
            </Button>
          </Box>
        )}

        {messages.map((message: PayloadMessage, idx: number) => {
          const isSended = Boolean(message.userKey);
          const isFirstMessage = Boolean(idx === 0);
          const isLastMessage = Boolean(messages.length === idx + 1);
          const isMessageOver =
            Boolean(messages[idx + 1] && messages[idx + 1].userKey) !==
            isSended;
          const isImage = message.messageType === PayloadMessageType.IMAGE;
          const isFirstOfBlock = !isFirstMessage
            ? Boolean(messages[idx - 1].userKey) !== isSended
            : true;

          // const MessageHeader = () => {
          //   return (
          //     <Box>
          //       <Typography sx={{ fontWeight: "bold", fontSize: "0.8em" }}>
          //         {/* {!message.userKey &&
          //           (nomeBotAtendimento
          //             ? nomeBotAtendimento
          //             : message.senderName)} */}
          //       </Typography>
          //     </Box>
          //   );
          // };

          const componentDecorator = (href: any, text: any) => (
            <Tooltip
              key={message._id}
              title="Clique para abrir o link em uma nova guia."
            >
              <Link
                href={href}
                key={message._id}
                target="_blank"
                underline="none"
                rel="noopener noreferrer"
              >
                {text}
              </Link>
            </Tooltip>
          );

          const MessageBody = () => {
            const [backdrop, setBackdrop] = useState(false);
            // console.log("MESSAGE", message);

            switch (message.messageType) {
              case PayloadMessageType.AUDIO:
                return (
                  <Box>
                    {message.audio && <AudioPlayer src={message.audio?.externalUrl ?? message.audio?.url} />}

                    {/* <Link
                      href={message.audio?.url}
                      target="_blank"
                      underline="none"
                      rel="noopener noreferrer"
                    >
                      Audio
                    </Link> */}
                  </Box>
                );
              case PayloadMessageType.VIDEO:
                return (
                  <Box>
                    {message.video && <VideoPlayer src={message.video?.externalUrl ?? message.video?.url} />}

                    {/* <Link
                      href={message.video?.url}
                      target="_blank"
                      underline="none"
                      rel="noopener noreferrer"
                    >
                      Video
                    </Link> */}
                  </Box>
                );

              case PayloadMessageType.IMAGE:
                return (
                  <Box
                    sx={{
                      " ::before": {
                        content: '""',
                        display: "block",
                        marginTop: "100%",
                      },
                      position: "relative",
                      cursor: "pointer",
                    }}
                    onClick={() => onExpandImage(message?.image?.externalUrl ?? message?.document?.url)}
                  >
                    <Tooltip
                      key={message._id}
                      title="Clique para visualizar a imagem por completo."
                    >
                      <img
                        src={message?.image?.externalUrl ?? message?.image?.url}
                        style={{
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          left: 0,
                          right: 0,
                          width: "100%",
                          height: "98%",
                          borderRadius: 8,
                          objectFit: "cover",
                          objectPosition: "center",
                        }}
                        alt="imagem"
                      />
                    </Tooltip>
                  </Box>
                );

              case PayloadMessageType.DOCUMENT:
                if (message?.document?.mimeType === "application/pdf") {
                  return (
                    <Box
                      sx={{ position: "relative" }}
                      onMouseEnter={() => setBackdrop(true)}
                      onMouseLeave={() => setBackdrop(false)}
                    >
                      <Backdrop
                        open={backdrop}
                        sx={{
                          position: "absolute",
                        }}
                      >
                        <Tooltip
                          key={message._id}
                          title="Clique para visualizar o documento em uma nova guia."
                        >
                          <Link
                            target="_blank"
                            href={message.document?.externalUrl ?? message.document?.url}
                            rel="noreferrer"
                          >
                            <Button variant="contained">Abrir documento</Button>
                          </Link>
                        </Tooltip>
                      </Backdrop>

                      <object
                        data={message.document?.externalUrl ?? message.document?.url}
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        style={{}}
                      >
                        <Link
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: isDarkMode
                              ? theme.palette.primary.main
                              : "#FFFFFF",
                          }}
                          target={"_blank"}
                          href={message.document?.externalUrl ?? message.document?.url}
                        >
                          <Description fontSize="large" color="error" />
                          <Typography
                            sx={{
                              marginLeft: 2,
                              marginRight: 2,
                              ...(isDarkMode
                                ? { color: "#FFFFFF" }
                                : { color: theme.palette.primary.main }),
                            }}
                          >
                            Arquivo
                          </Typography>
                        </Link>
                      </object>
                    </Box>
                  );
                } else {
                  let endpoint = "https://mmd.futurotec.com.br";
                  let messageUrl = message.document?.url;

                  return (
                    <>
                      {endpoint === messageUrl?.split("/api")[0] ? (
                        <Box>
                          <Tooltip
                            key={message._id}
                            title="Clique para visualizar o documento em uma nova guia."
                          >
                            <Link
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: isDarkMode
                                  ? theme.palette.primary.main
                                  : "#FFFFFF",
                              }}
                              target={"_blank"}
                              href={message.document?.externalUrl ?? message.document?.url}
                            >
                              <Description
                                fontSize="large"
                                sx={{
                                  fill: isDarkMode
                                    ? "#FFFFFF"
                                    : theme.palette.primary.main,
                                }}
                              />
                              <Typography
                                sx={{
                                  marginLeft: 2,
                                  marginRight: 2,
                                  ...(isDarkMode
                                    ? { color: "#FFFFFF" }
                                    : { color: theme.palette.primary.main }),
                                }}
                              >
                                Arquivo
                              </Typography>
                            </Link>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box>
                          <Typography
                            sx={{
                              whiteSpace: "pre-wrap",
                              overflowWrap: "break-word",
                            }}
                          >
                            <Linkify componentDecorator={componentDecorator}>
                              {message.document?.externalUrl ?? message?.document?.url}
                            </Linkify>
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              width: "74px",
                              display: "inline-block",
                              float: "right",
                              verticalAlign: "middle",
                            }}
                          ></Typography>
                        </Box>
                      )}
                    </>
                  );
                }

              case PayloadMessageType.TEXT:
                return (
                  <Box>
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                      }}
                    >
                      <Linkify componentDecorator={componentDecorator}>
                        {message.text}
                      </Linkify>
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        width: "74px",
                        display: "inline-block",
                        float: "right",
                        verticalAlign: "middle",
                      }}
                    ></Typography>
                  </Box>
                );
              default:
                return <Box></Box>;
            }
          };

          const MessageFooter = () => {
            const messageTime = message?.createdAt;
            const momentTime = moment(messageTime).format("HH:mm");
            return (
              <>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "3px",
                    right: "5px",
                    height: "15px",
                    lineHeight: "15px",
                  }}
                >
                  <Typography sx={{ fontSize: "11px" }}>
                    {messageTime ? momentTime : "--:--"}
                  </Typography>
                </Box>
                {isFirstOfBlock && (
                  <SvgIcon
                    sx={{
                      ...styles.messageBubbleTail,
                      ...(isSended ? styles.sendedMessageBubbleTail : {}),
                    }}
                  >
                    <path
                      opacity=".13"
                      d="M5.188 1H0v11.193l6.467-8.625C7.526 2.156 6.958 1 5.188 1z"
                    ></path>
                    <path
                      fill="currentColor"
                      d="M5.188 0H0v11.193l6.467-8.625C7.526 1.156 6.958 0 5.188 0z"
                    ></path>
                  </SvgIcon>
                )}
              </>
            );
          };

          if (message.text === `Iniciar atendimento`) {
            return null;
          }

          return (
            <Box
              key={message._id}
              sx={{
                ...styles.messageWrapper,
                ...(isSended ? styles.sendedMessageWrapper : {}),
              }}
            >
              <Box
                sx={{
                  ...styles.message,
                  ...(isFirstMessage || isFirstOfBlock
                    ? isSended
                      ? styles.borderedSendedMessage
                      : styles.borderedMessage
                    : {}),
                  ...(isLastMessage || isMessageOver
                    ? styles.messageSpacing
                    : {}),
                  ...(isFirstMessage ? styles.firstMessage : {}),
                  ...(isSended ? styles.sendedMessage : {}),
                  ...(isImage ? styles.imageMessage : {}),
                }}
              >
                {/* <MessageHeader /> */}
                <MessageBody />
                <MessageFooter />
              </Box>
            </Box>
          );
        })}
      </>
    );
  }

  const AlwaysScrollToBottom = ({ messages }: any) => {
    const elementRef = useRef<null | HTMLDivElement>(null);
    useEffect(() => {
      // console.log("scroll to bottom", messages);
      if (messages.length > 0 /*&& messages[messages.length - 1].userKey*/) {
        elementRef.current && elementRef.current.scrollIntoView();
      }
    }, [messages]);
    return <div ref={elementRef} />;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: theme.chat.background,
      }}
    >
      <Box sx={{ zIndex: 2 }}>
        <AppBar position="static" sx={styles.appBar}>
          <Toolbar variant="dense">
            <Typography variant="h6" component="div" sx={styles.headerText}>
              {boasVindas ? boasVindas : "Escallo Chat"}
            </Typography>
            {darkModeEnabled && (
              <IconButton
                sx={{ mr: 1 }}
                aria-label="Alternar modo escuro"
                onClick={() => {
                  dispatch(toggleTheme());
                }}
              >
                {isDarkMode ? <LightMode /> : <DarkMode />}
              </IconButton>
            )}
            {/*!isAnonymousLogin &&*/ logoutEnabled && (
              <Tooltip title="Finalizar" >
              <IconButton aria-label="Sair" onClick={onLogout}>
                <Logout />
              </IconButton>
            </Tooltip>
            )}
          </Toolbar>
        </AppBar>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column-reverse",
          paddingLeft: "10%",
          paddingRight: "10%",
          zIndex: 1,
          margin: `0 4px`,
          flex: 1,
          overflow: "overlay",
        }}
      >
        {chatLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              position: "absolute",
              right: 0,
              left: 0,
              top: 0,
              bottom: 0,
              alignItems: "center",
              background: "rgba(0,0,0,0.5)",
              zIndex: 999999,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <div>
          <Messages messages={messages} />
          <AlwaysScrollToBottom messages={newMessages} />
        </div>
      </Box>

      <BottomChat
        styles={styles}
        disabled={chatLoading}
        send={{
          image: sendImage,
          document: sendDocument,
          video: sendVideo,
          message: sendMessage,
        }}
      />
      <Modal
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          margin: "30px",
        }}
        open={Boolean(imageModal)}
        onClose={() => setImageModal("")}
        BackdropProps={{
          timeout: 500,
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: "0.5rem",
            outline: "none",
          }}
        >
          <Magnifier
            dragToMove={false}
            imageSrc={imageModal}
            imageAlt="Imagem completa"
            
          />

          <Link
            style={{ color: "#fff" }}
            href={imageModal}
            target="_blank"
            underline="none"
            rel="noopener noreferrer"
          >
            Abrir em uma nova guia
          </Link>
        </Box>
      </Modal>
    </Box>
  );
}

function useChatTheme() {
  const theme = useTheme();
  // const window: Styles = {};
  const sendWrapper: Styles = {
    [theme.breakpoints.down("sm")]: {
      padding: `${theme.spacing(1.5)} ${theme.spacing(1)}`,
    },
    [theme.breakpoints.up("sm")]: {
      padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
    },
  };
  const appBar: Styles = {
    background: theme.palette.background.paper,
  };
  const textInput: Styles = {
    "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
      padding: `0 ${theme.spacing(0.5)}`,
    },
    "& .MuiOutlinedInput-root": {
      padding: `${theme.spacing(1.2)} ${theme.spacing(1)}`,
      background: theme.textInput.background,
    },
    marginRight: 1.5,
    marginLeft: 1.5,
  };
  const bottom: Styles = {
    display: "flex",
    flexDirection: "column",
    zIndex: 2,
    boxShadow:
      "0px -2px 4px -1px rgb(0 0 0 / 20%), 0px -4px 5px 0px rgb(0 0 0 / 14%), 0px -1px 10px 0px rgb(0 0 0 / 12%)",
    justifyContent: "center",
    background: theme.palette.background.paper,
  };
  const sendForm: Styles = {
    display: "flex",
    height: "100%",
  };

  const messageWrapper: Styles = {
    display: "flex",
    justifyContent: "flex-start",
  };
  const sendedMessageWrapper: Styles = {
    justifyContent: "flex-end",
  };

  const messageBubbleTail: Styles = {
    fontSize: 30,
    position: "absolute",
    top: 0,
    transform: "scaleX(-1)",
    left: -30,
    color: theme.messageBox.received.background,
  };
  const sendedMessageBubbleTail: Styles = {
    transform: "initial",
    left: "initial",
    right: -30,
    color: theme.messageBox.sended.background,
  };

  const message: Styles = {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "85%",
    },
    [theme.breakpoints.up("sm")]: {
      maxWidth: "75%",
    },
    [theme.breakpoints.up("md")]: {
      maxWidth: "60%",
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: "50%",
    },
    padding: 1.6,
    marginBottom: "2px",
    paddingBottom: 2.4,
    display: "flex",
    position: "relative",
    flexDirection: "column",
    borderRadius: 1.2,
    background: theme.messageBox.received.background,
    listStyle: "none",
  };

  const imageMessage: Styles = {
    [theme.breakpoints.down("lg")]: {
      width: "100%",
      maxWidth: "350px",
    },
    [theme.breakpoints.up("lg")]: {
      width: "100%",
      maxWidth: "350px",
    },
  };

  const borderedMessage: Styles = {
    borderTopLeftRadius: 0,
  };
  const borderedSendedMessage: Styles = {
    borderTopRightRadius: 0,
  };

  const firstMessage: Styles = {
    marginTop: "12px",
  };

  const messageSpacing: Styles = {
    marginBottom: "12px",
  };

  const sendedMessage: Styles = {
    background: theme.messageBox.sended.background,
  };

  const speedDial: Styles = {
    "& .MuiSpeedDialAction-fab": {
      width: "calc( 4rem - 20px )",
      height: "calc( 4rem - 20px )",
    },
    "& .MuiSpeedDialIcon-openIcon, .MuiSpeedDialIcon-icon": {
      color: theme.palette.action.active,
    },
    "& .MuiSpeedDialIcon-openIcon:hover, .MuiSpeedDialIcon-icon:hover": {
      color: theme.palette.action.hover,
    },
    "& .MuiSpeedDial-actions": {
      marginBottom: "10px",
    },
    "& .MuiSpeedDial-fab:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.08)",
    },
    "& .MuiSpeedDial-fab": {
      boxShadow: "none",
      backgroundColor: "transparent",
      height: "calc( 4rem - 24px )",
      width: "calc( 4rem - 24px )",
      position: "absolute",
    },
    width: "calc( 4rem - 24px )",
    height: "calc( 4rem - 24px )",
  };
  const headerText: Styles = {
    color: theme.palette.text.primary,
    flexGrow: 1,
  };
  // const svgFile: Styles = {
  //   fill: theme.palette.text.primary,
  // };
  return {
    theme,
    styles: {
      sendForm,
      headerText,
      speedDial,
      sendWrapper,
      textInput,
      appBar,
      messageWrapper,
      sendedMessageWrapper,
      messageBubbleTail,
      sendedMessageBubbleTail,
      message,
      imageMessage,
      borderedMessage,
      borderedSendedMessage,
      firstMessage,
      messageSpacing,
      sendedMessage,
      bottom,
    },
  };
}
